type DataWithNormalPosition = {
    normalPosition: number;
};

/**
 * Function that can be used to compare two objects of type {@link DataWithNormalPosition} to order them.\
 * This will return -1 if {@link a}'s {@link DataWithNormalPosition.normalPosition normalPosition} is smaller than {@link b}'s {@link DataWithNormalPosition.normalPosition normalPosition}.\
 * 1 if {@link a}'s {@link DataWithNormalPosition.normalPosition normalPosition} is greater than {@link b}'s {@link DataWithNormalPosition.normalPosition normalPosition}.\
 * 0 if the {@link DataWithNormalPosition.normalPosition normalPositions} are equal.
 *
 * @param a The data that is being compared.
 * @param b The data that is being compared to.
 * @returns -1 if {@link a}'s {@link DataWithNormalPosition.normalPosition normalPosition} is smaller than {@link b}'s {@link DataWithNormalPosition.normalPosition normalPosition},\
 * 1 if {@link a}'s {@link DataWithNormalPosition.normalPosition normalPosition} is greater than {@link b}'s {@link DataWithNormalPosition.normalPosition normalPosition} or\
 * 0 if the {@link DataWithNormalPosition.normalPosition normalPositions} are equal.
 */
export function SliceDataComparison(
    a: DataWithNormalPosition,
    b: DataWithNormalPosition
): number {
    return Math.sign(a.normalPosition - b.normalPosition);
}

/**
 * {@link SliceData} is a datamodel for slice data.
 *
 * Slices have a {@link SliceData._normalPosition normalPosition}, that is the slice's position along the normal axis.\
 * This normal position is relative to PatientSpace origin {0, 0, 0}.\
 * Calculated as a dot product between any arbitrary point on the plane and the plane's normal direction.
 *
 * Slice may also have a thickness, which is stored within the {@link sliceThickness}-parameter.\
 * If Slice has no thickness, it's thickness defaults to 0.
 */
export class SliceData {
    /**
     * Slice's position along the normal axis.\
     * This normal position is relative to PatientSpace origin {0, 0, 0}.\
     * Calculated as a dot product between any arbitrary point on the plane and the plane's normal direction.
     */
    private _normalPosition: number;

    /**
     * Slice thickness. May be 0, if slice thickness is not defined.
     */
    public readonly sliceThickness: number;

    /**
     * The index of this {@link SliceData Slice} within the {@link SliceSource}.
     */
    private _sliceIndex: number;

    public constructor(normalPosition: number);

    public constructor(normalPosition: number, sliceThickness: number);

    public constructor(ref: SliceData);

    /**
     * Method to construct a {@link SliceData}-object
     * @param normalPosition The normal position of this slice relative to PatientSpace origin {0, 0, 0}.\
     * Calculated as a dot product between any arbitrary point on the plane and the plane's normal direction.\
     * When constructing multiple {@link SliceData}-objects that share the same normal direction,\
     * it may be adviced to use points that share the same projection on the plane.
     * @param sliceThickness Optional {@link sliceThickness}-parameter defining the Slice's thickness.
     */
    public constructor(arg0: number | SliceData, sliceThickness = 0) {
        if (arg0 instanceof SliceData) {
            this._normalPosition = arg0._normalPosition;
            this.sliceThickness = arg0.sliceThickness;
        } else {
            this._normalPosition = arg0;
            this.sliceThickness = sliceThickness;
        }
        this._sliceIndex = 0;
    }

    public incrementSliceIndex(): number {
        return ++this._sliceIndex;
    }

    public get sliceIndex(): number {
        return this._sliceIndex;
    }

    public setSliceIndex(sliceIndex: number): SliceData {
        this._sliceIndex = sliceIndex;
        return this;
    }

    /**
     * Method to get the {@link _normalPosition normalPosition} of this {@link SliceData slice}.
     */
    public get normalPosition(): number {
        return this._normalPosition;
    }

    /**
     * Method to get half {@link sliceThickness slice thickness}.\
     * When generating {@link SliceMesh}, we generate vertices half thickness away.
     */
    public get halfSliceThickness(): number {
        return this.sliceThickness * 0.5;
    }

    /**
     * Method to invert the {@link _normalPosition normalPosition} of this {@link SliceData slice}.\
     * This should be called whenever the normal direction is inverted.
     */
    public flipNormal(): void {
        this._normalPosition = -this._normalPosition;
    }
}
